import React, { useContext, useState } from 'react';

import { LayoutContext } from '@/components/Layout';
import { BottomActionLayout } from '@/components/shared/BottomActionLayout/BottomActionLayout';
import { AsaphButton } from '@/components/shared/Button';
import {
	CONGREGATIONAL_STEP_LABEL_MAP,
	PERSONAL_STEP_LABEL_MAP,
	TEAM_STEP_LABEL_MAP,
} from '@/config/constants';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useLayout } from '@/providers/LayoutProvider';
import { LimitedTeamSong } from '@/providers/TeamSongsProvider';

import {
	emptyTeamSongFeedback,
	TeamSongFeedbackScoresMap,
	TeamSongFeedbackSubmission,
} from './FeedbackPanel';
import styles from './FeedbackPanel.module.scss';
import { FeedbackScaleSelector } from './FeedbackScaleSelector';
import { FeedbackSection } from './FeedbackSection';

export const TeamSongScorePage: React.FC<{
	teamSong?: LimitedTeamSong;
	teamSongSubmission?: TeamSongFeedbackSubmission;
	currentTeamSongFeedbackPageIdx: number;
	setTeamSongFeedbackScoresMap: React.Dispatch<
		React.SetStateAction<TeamSongFeedbackScoresMap>
	>;
	setCurrentTeamSongFeedbackPageIdx: React.Dispatch<
		React.SetStateAction<number>
	>;
}> = ({
	teamSong,
	teamSongSubmission,
	currentTeamSongFeedbackPageIdx,
	setTeamSongFeedbackScoresMap,
	setCurrentTeamSongFeedbackPageIdx,
}) => {
	if (!teamSong || !teamSongSubmission) {
		return null;
	}

	const updateTeamSongFeedbackScoresMap = (
		submissionScores: TeamSongFeedbackSubmission,
	) =>
		setTeamSongFeedbackScoresMap((submissionsMap) => {
			return {
				...submissionsMap,
				[teamSong.id]: submissionScores,
			};
		});

	return (
		<BottomActionLayout
			actionDivider
			listClassName={styles['feedback-list']}
			actionContent={
				<>
					{currentTeamSongFeedbackPageIdx > 0 && (
						<AsaphButton
							color="grey"
							label="Back"
							onClick={() => {
								setCurrentTeamSongFeedbackPageIdx((i) => {
									const nextValue = i - 1;
									if (nextValue < 0) {
										return 0;
									}
									return nextValue;
								});
							}}
							fullWidth
						/>
					)}
					<AsaphButton
						color="blue"
						label="Next"
						fullWidth
						onClick={() => {
							setCurrentTeamSongFeedbackPageIdx((i) => i + 1);
						}}
					/>
				</>
			}
		>
			<FeedbackSection
				title="Personal Worship"
				subtitle="How well did this song lead you in worship?"
			>
				<FeedbackScaleSelector
					stepLabelMap={PERSONAL_STEP_LABEL_MAP}
					value={teamSongSubmission.personalScore}
					onChange={(value) => {
						updateTeamSongFeedbackScoresMap({
							...teamSongSubmission,
							personalScore: value,
						});
					}}
				/>
			</FeedbackSection>
			<FeedbackSection
				title="Team Execution"
				subtitle="How well did your team lead this song?"
			>
				<FeedbackScaleSelector
					stepLabelMap={TEAM_STEP_LABEL_MAP}
					value={teamSongSubmission.teamScore}
					onChange={(value) => {
						updateTeamSongFeedbackScoresMap({
							...teamSongSubmission,
							teamScore: value,
						});
					}}
				/>
			</FeedbackSection>
			<FeedbackSection
				title="Church Engagement"
				subtitle="How well did your people sing this song?"
			>
				<FeedbackScaleSelector
					stepLabelMap={CONGREGATIONAL_STEP_LABEL_MAP}
					value={teamSongSubmission.congregationalScore}
					onChange={(value) => {
						updateTeamSongFeedbackScoresMap({
							...teamSongSubmission,
							congregationalScore: value,
						});
					}}
				/>
			</FeedbackSection>
		</BottomActionLayout>
	);
};
